import { notification } from "antd";
import { get } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useContext, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import Loading from "shared/components/Spin";
import { useLazyQuery, useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { StoreContext } from "shared/store";
import {
  clearProjectRuntime,
  persistProcessRuntime,
} from "screens/Private/Broker/Projects/Detail/resolveProcessRuntime";
import { isUnavailable } from "screens/Public/Processes/Unavailable/sourceAvailability";
import { CREATE_PROJECT } from "utils/api/graphql/mutations/projects";
import {
  SOURCE_AVAILABILITY,
  SOURCE_TRACER,
} from "utils/api/graphql/queries/sources";
import { TRACER } from "utils/api/graphql/queries/tracers";
import { SCREENS } from "utils/constants";
import { clearPaginationHistory } from "utils/helpers/pagination";
import { buildNewProjectData, resolveFirstProcessStatus } from "../newProject";

const FromSource = () => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const navigate = useNavigate();
  const { sourceId } = useParams();
  const { user } = useContext(StoreContext);
  const userId = get(user, "id");
  const started = useRef(false);
  const [getAvailability] = useLazyQuery(SOURCE_AVAILABILITY, {
    fetchPolicy: "no-cache",
  });
  const [getSourceTracer] = useLazyQuery(SOURCE_TRACER, {
    fetchPolicy: "no-cache",
  });
  const [getTracer] = useLazyQuery(TRACER, { fetchPolicy: "no-cache" });
  const [addProject] = useMutation(CREATE_PROJECT);

  useEffect(() => {
    if (started.current || !userId) return;
    started.current = true;

    const leave = (messageKey) => {
      notification.error({ message: t(messageKey) });
      navigate(SCREENS.PRIVATE.BROKER.PROJECTS.LIST.path, { replace: true });
    };

    const openProject = async () => {
      const where = { id: sourceId };
      const { data: availabilityData } = await getAvailability({
        variables: { where },
      });
      const status = get(availabilityData, "sourceAvailability.status");
      if (isUnavailable(status)) {
        return navigate(
          `${SCREENS.PUBLIC.PROCESSES.UNAVAILABLE.path}/${status}`,
          { replace: true },
        );
      }

      const { data: sourceData } = await getSourceTracer({
        variables: { where },
      });
      const tracerId = get(sourceData, "source.tracer.id");
      if (!tracerId) return leave("FROM_SOURCE.NOT_FOUND");

      const { data: tracerData } = await getTracer({
        variables: { where: { id: tracerId } },
      });
      const tracer = omitDeep(get(tracerData, "tracer"), "__typename");
      if (!get(tracer, "id")) return leave("FROM_SOURCE.NOT_FOUND");

      clearProjectRuntime();
      const { data: projectData } = await addProject({
        variables: { data: buildNewProjectData({ tracer, userId }) },
      });
      const projectId = get(projectData, "addProject.id");
      if (!projectId) return leave("FROM_SOURCE.FAILED");

      const process = get(tracer, "flow.process");
      const firstStatus = resolveFirstProcessStatus(process);
      clearPaginationHistory("projects");
      persistProcessRuntime(projectId, process, tracer, get(firstStatus, "id"));
      return navigate(
        `${SCREENS.PRIVATE.BROKER.PROJECTS.DETAIL.path}/${projectId}`,
        { replace: true, state: { process, tracer, status: firstStatus } },
      );
    };

    openProject().catch(() => leave("FROM_SOURCE.FAILED"));
  }, [
    t,
    userId,
    sourceId,
    navigate,
    getAvailability,
    getSourceTracer,
    getTracer,
    addProject,
  ]);

  return <Loading />;
};

export default FromSource;
